import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type Game } from '@goodbuild/shared';
import { Button, Callout, PageHead, ShelfHead } from '../ui/index.js';
import { useAccess, useGames, useLibraryUpdates } from '../api/hooks.js';
import { useInstalledGames } from '../components/game/useInstallState.js';
import { UninstallConfirm } from '../components/game/InstallButton.js';
import { Focusable, FocusZone } from '../components/focus/Focusable.js';
import { Menu, MenuButton, type MenuOption } from '../components/focus/Menu.js';
import { GameCard } from '../components/game/GameCard.js';
import { ShelfBar } from '../components/game/ShelfBar.js';
import { SupporterCta } from '../components/layout/SupporterCta.js';
import { EmptyState, Loading } from '../components/layout/Loading.js';
import { useGamepadButton } from '../components/focus/useGamepad.js';
import { useDownloads } from '../downloads/store.js';
import { markPlayed, useLastPlayed } from '../library/recent.js';
import {
  isLibrarySort,
  LIBRARY_SORT_LABELS,
  LIBRARY_SORTS,
  matchesQuery,
  newestBuildAt,
  normalise,
  sortEntries,
  tileActions,
  tilePrimary,
  tileStatus,
  type LibraryAction,
  type LibraryEntry,
  type LibrarySort,
} from '../library/view.js';
import { buildPlatform, capabilities } from '../platform/index.js';
import { native } from '../platform/tauri.js';
import { launchGame } from '../analytics/wire.js';
import { errorMessage } from '../lib/errors.js';

export const Route = createFileRoute('/library')({
  component: Library,
  /**
   * The sort lives in the address.
   *
   * So that a library somebody arranged by what they last played is still arranged that way when
   * they come back to it. It is the only thing left in there: the rungs and the Recent row were
   * in the address too, and a screen with one grid on it has nothing else to say about itself.
   */
  validateSearch: (search: Record<string, unknown>): { sort?: LibrarySort } => ({
    ...(isLibrarySort(search.sort) ? { sort: search.sort } : {}),
  }),
});

/**
 * The library: every game this account can open, in one grid (`03-client-app.md` section 4.2).
 *
 * One grid, and nothing above it but the toolbar. No Recent row, no rungs to narrow down to, no
 * All to come back from — a library is a small room, and a room of a few dozen games does not
 * need dividing before it can be read. Sorting by what was last played says what the Recent row
 * said, in the order the whole shelf is already in, and a game sitting in both that row and the
 * grid under it is a game somebody can find twice and start twice.
 *
 * What is in here is what the account can open: everything, for a supporter, plus anything bought
 * outright — installed or not. The shelf used to hold only what was on the disk, which made the
 * answer to "what do I have?" a list of what somebody had already fetched, and opened on an empty
 * room on a new machine with a paid-up membership behind it. So a game that is not here yet is on
 * the shelf in grey with Install on it, and fills its colour in from the left as the bytes land
 * (`GameCard.tsx`). Epic puts a small bar under the name and leaves the capsule alone; a shelf
 * where the picture itself says what is here and what is arriving reads from the sofa, which is
 * where this app is used.
 *
 * A tile does the thing rather than describing it. Pressing a game plays it, starts it arriving,
 * or gets what is arriving moving again, and the page with the screenshots and the notes on it
 * moves to the "…" alongside everything else you might pick deliberately. Epic opens the page; a
 * page is the right answer for a game somebody has not decided about, and every game in here came
 * with something they have already paid for. The undecided ones are next door on Games, where a
 * press opens the page and does nothing else (`games.index.tsx`).
 *
 * The screen is behind Access or a purchase rather than only the actions on it. One of the two is
 * the only way anything gets in here, so an empty grid would read as a fault rather than as a
 * price — and the shelf that sells the membership is Games, which is open to everyone.
 */
function Library() {
  const navigate = useNavigate();
  const { sort = 'alpha' } = Route.useSearch();
  const { hasAccess, loading: accessLoading } = useAccess();
  const { data, isLoading } = useGames();
  const { games: installed } = useInstalledGames();
  const { byGame } = useDownloads();
  const lastPlayed = useLastPlayed();
  const updates = useLibraryUpdates(
    installed.map((g) => ({ gameId: g.gameId, buildId: g.buildId })),
  );

  /** Held here rather than in the address: this filters what is already on the screen. */
  const [query, setQuery] = React.useState('');
  /** The game whose "…" is open. One at a time, like every menu. */
  const [menuFor, setMenuFor] = React.useState<string | null>(null);
  const [confirm, setConfirm] = React.useState<Game | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const canInstall = capabilities.installWindowsGames || capabilities.installAndroidGames;
  /** Whether "Open folder" is worth offering: a desktop has a file manager, a phone has not. */
  const canReveal = capabilities.revealFiles;
  const platform = buildPlatform;

  // Written on its own rather than over whatever is already in the address: Games calls a
  // parameter `sort` as well and means a shorter list of values by it, so a sort carried over
  // from that screen would be a sort this one cannot honour.
  const setSort = React.useCallback(
    (next: LibrarySort) => void navigate({ to: '/library', search: { sort: next }, replace: true }),
    [navigate],
  );

  // B and Escape drop the uninstall question, the way out of every overlay in this app. The
  // shell's own back steps aside while a sheet is up, so without these the only way out of it
  // is to find Cancel.
  useGamepadButton('b', () => setConfirm(null), confirm !== null);
  React.useEffect(() => {
    if (!confirm) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setConfirm(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [confirm]);

  const all = React.useMemo(() => data ?? [], [data]);
  const updateIds = React.useMemo(
    () => new Set((updates.data?.updates ?? []).map((u) => u.gameId)),
    [updates.data],
  );

  /**
   * Every game, with what it is doing on this machine worked out once.
   *
   * Three sources meet here and nowhere else — the install list, the download queue and the
   * API's answer about newer builds — so a tile is only ever a picture of a settled state
   * rather than a place where three of them are reconciled fifty times over.
   */
  const entries: LibraryEntry[] = React.useMemo(() => {
    const onDisk = new Map(installed.map((game) => [game.gameId, game]));
    return all.map((game) => {
      const here = onDisk.get(game.id) ?? null;
      return {
        game,
        view: tileStatus({
          availableHere: platform === null || game.builds.some((b) => b.platform === platform),
          hasAccess,
          installed: Boolean(here),
          hasUpdate: updateIds.has(game.id),
          job: byGame[game.id] ?? null,
        }),
        installedAt: here?.installedAt ?? null,
        sizeBytes: here?.sizeBytes ?? null,
        lastPlayedAt: lastPlayed[game.id] ?? null,
        publishedAt: newestBuildAt(game),
      };
    });
  }, [all, installed, byGame, updateIds, lastPlayed, hasAccess, platform]);

  /**
   * Games with a press already being carried out.
   *
   * A capsule is now one big button that plays the game, and a capsule is the easiest thing in
   * this app to hit twice — every desktop ever made has trained people to double-click a picture
   * to start a program. Install twice is caught by the queue, which hands back the job it
   * already has; launch twice is two copies of the game running against each other. So the
   * second press is dropped until the first has come back.
   *
   * A ref rather than state: it guards the call, and nothing on the screen should flicker
   * because of it. What the tile shows is the state the job reports a moment later.
   */
  const inFlight = React.useRef<Set<string>>(new Set());

  /** Runs one item of a tile's menu, or the press on the tile itself. Native calls all catch. */
  const act = React.useCallback(
    async (entry: LibraryEntry, action: LibraryAction) => {
      const { game } = entry;
      if (action === 'open') {
        void navigate({ to: '/games/$slug', params: { slug: game.slug } });
        return;
      }
      if (action === 'downloads') return void navigate({ to: '/downloads' });
      if (action === 'join') return void navigate({ to: '/join' });
      if (action === 'app') return void navigate({ to: '/download' });
      // Uninstalling on Linux asks about the Proton prefix first, because saves often live
      // inside it. Everything else happens the moment it is picked.
      if (action === 'uninstall') return setConfirm(game);

      if (inFlight.current.has(game.id)) return;
      inFlight.current.add(game.id);
      setError(null);
      try {
        switch (action) {
          case 'install':
            if (platform) await native.gameInstall(game.id, game.slug, platform);
            break;
          case 'update':
            await native.gameUpdate(game.id);
            break;
          case 'play':
            await launchGame(game.id);
            // The native event says so too, a moment later. Writing it here as well means a
            // shelf arranged by what was last played has already moved by the time the game's
            // window opens.
            markPlayed(game.id);
            break;
          case 'pause':
            await native.gamePause(game.id);
            break;
          case 'resume':
            await native.gameResume(game.id);
            break;
          case 'cancel':
            await native.gameCancel(game.id);
            break;
          case 'verify':
            await native.gameVerify(game.id);
            break;
          case 'reveal':
            await native.gameReveal(game.id);
            break;
        }
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        inFlight.current.delete(game.id);
      }
    },
    [navigate, platform],
  );

  const uninstall = React.useCallback(async (game: Game, deletePrefix: boolean) => {
    setConfirm(null);
    setError(null);
    try {
      await native.gameUninstall(game.id, deletePrefix);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  /**
   * The library, for somebody with neither a membership nor a game of their own.
   *
   * Games stays open to everyone: that shelf is what sells the membership, and a catalogue
   * behind a gate is a shop with the shutters down. This screen is the half a membership
   * actually buys, so an empty grid would read as a bug; it says what is missing and what it
   * costs instead.
   */
  if (!hasAccess) {
    // Access is not known on the first paint, and a supporter should not see the gate blink
    // past on the way to their own shelf.
    if (accessLoading) return <Loading label="Reading the shelf" />;
    return (
      <FocusZone focusKey="library" direction="column" className="gb-stack gb-stack--loose">
        <PageHead
          eyebrow="Yours"
          title="Your library."
          description="Supporters install everything we make and play it here. This is where it lands."
        />
        <SupporterCta title="Your library opens with a membership." autoFocus />
      </FocusZone>
    );
  }

  if (isLoading) return <Loading label="Reading the shelf" />;

  const needle = normalise(query);

  /**
   * What this account can open.
   *
   * A membership covers the whole catalogue, including the rungs nothing is sold on; a purchase
   * covers exactly the game it bought and is not undone by a membership lapsing. So both are
   * asked, and anything else we make stays on Games, where it is a game to read about rather
   * than a game to play.
   */
  const mine = hasAccess ? entries : [];
  const shown = mine.filter((entry) => matchesQuery(entry.game, needle));
  const arranged = sortEntries(shown, sort);

  return (
    <FocusZone focusKey="library" direction="column" className="gb-stack gb-stack--loose">
      <PageHead
        eyebrow="Yours"
        title="Your library."
        description="Every game you can play. Press one to play it, or to bring it down here."
      />

      {error ? (
        <Callout.Root color="danger">
          <Callout.Description>{error}</Callout.Description>
          <div className="gb-row">
            <Focusable onSelect={() => setError(null)} selectLabel="Dismiss">
              <Button size="2" variant="ghost">
                {'Dismiss'}
              </Button>
            </Focusable>
          </div>
        </Callout.Root>
      ) : null}

      {/* Search and sort — the only two things one undivided grid is arranged by. */}
      <ShelfBar query={query} onQuery={setQuery} focusKey="library-bar" label="Search the shelf">
        <MenuButton
          label="Sort"
          value={LIBRARY_SORT_LABELS[sort]}
          focusKey="library-sort"
          align="end"
          options={LIBRARY_SORTS.map<MenuOption<LibrarySort>>((key) => ({
            id: key,
            label: LIBRARY_SORT_LABELS[key],
            checked: key === sort,
          }))}
          onPick={setSort}
        />
      </ShelfBar>

      {arranged.length === 0 ? (
        <EmptyState
          title={needle.length > 0 ? `Nothing matches “${query}”.` : 'Nothing here yet.'}
          body={
            needle.length > 0
              ? 'Try a shorter word, or clear the search to see the whole shelf.'
              : 'Everything your membership opens lands here as soon as it is published.'
          }
        />
      ) : (
        <section className="gb-section">
          <ShelfHead
            title="Your games"
            note={`${arranged.length} ${arranged.length === 1 ? 'game' : 'games'}`}
          />
          <FocusZone focusKey="library-grid" className="gb-grid gb-grid--capsule">
            {arranged.map((entry, index) => (
              <LibraryCell
                key={entry.game.id}
                entry={entry}
                autoFocus={index === 0}
                canInstall={canInstall}
                canReveal={canReveal}
                menuFor={menuFor}
                onOpenMenu={setMenuFor}
                onCloseMenu={() => setMenuFor(null)}
                onAct={act}
              />
            ))}
          </FocusZone>
        </section>
      )}

      {confirm ? (
        <div
          className="gb-sheet-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={`Uninstall ${confirm.title}`}
        >
          <FocusZone trap focusKey="library-uninstall" direction="column">
            <UninstallConfirm
              gameTitle={confirm.title}
              onCancel={() => setConfirm(null)}
              onConfirm={(deletePrefix) => void uninstall(confirm, deletePrefix)}
            />
          </FocusZone>
        </div>
      ) : null}
    </FocusZone>
  );
}

/**
 * One tile and the menu that belongs to it.
 *
 * The tile is one big button and the press on it is the game's own next step — play it, install
 * it, resume the download — rather than a trip to its page. The menu is a sibling of the card
 * rather than a child because of exactly that: a menu living inside the button would launch the
 * game every time somebody picked Pause.
 */
function LibraryCell({
  entry,
  autoFocus,
  canInstall,
  canReveal,
  menuFor,
  onOpenMenu,
  onCloseMenu,
  onAct,
}: {
  entry: LibraryEntry;
  autoFocus: boolean;
  canInstall: boolean;
  canReveal: boolean;
  menuFor: string | null;
  onOpenMenu: (id: string) => void;
  onCloseMenu: () => void;
  onAct: (entry: LibraryEntry, action: LibraryAction) => void;
}) {
  const { game, view } = entry;
  /**
   * This tile's focus key.
   *
   * Prefixed, and one per game now that the grid is the only place a game appears: the
   * navigation library keys on this across the whole screen, and two tiles sharing a key are a
   * thumb that lands in both.
   */
  const id = `game-${game.id}`;
  const cell = React.useRef<HTMLDivElement>(null);
  const [side, setSide] = React.useState<'below' | 'above'>('below');
  const options = tileActions(view.state, canInstall, canReveal).map<MenuOption<LibraryAction>>(
    (action) => ({
      id: action.id,
      label: action.label,
      danger: action.danger,
    }),
  );

  /**
   * What the press on the tile does, and what A calls it.
   *
   * Worked out from the same state the tile is drawn from (`tilePrimary`), so what the picture
   * says and what pressing it does are one decision. The name of the game is kept as the label
   * for the presses that only open the page, because there "Game page" under the A glyph would
   * be saying less than the title does.
   */
  const primary = tilePrimary(view.state, canInstall);

  /**
   * Which way the menu opens.
   *
   * Measured when it is asked for rather than guessed from the row: a menu on the bottom row of
   * a grid that opens downwards is a menu off the end of the screen, and four items on a tile
   * near the top that opened upwards would be just as wrong.
   */
  const open = () => {
    const box = cell.current?.getBoundingClientRect();
    const room = box ? window.innerHeight - box.bottom : Infinity;
    // Estimated in rem rather than pixels: Big Picture grows the root and every item with it,
    // so a menu that fits on a desk can be half off the bottom of a television.
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    setSide(room < rem * (2.9 * options.length + 4) ? 'above' : 'below');
    onOpenMenu(id);
  };

  return (
    <div className="gb-library__cell" ref={cell}>
      <GameCard
        game={game}
        // A browser has no install state to draw, and a catalog greyed out from end to end
        // there would be describing a condition nobody can do anything about.
        view={canInstall ? view : null}
        caption="state"
        autoFocus={autoFocus}
        focusKey={id}
        onMore={open}
        onSelect={() => onAct(entry, primary.id)}
        selectLabel={primary.id === 'open' ? game.title : primary.label}
      />

      {menuFor === id ? (
        <Menu
          label={game.title}
          options={options}
          onPick={(action) => onAct(entry, action)}
          onClose={onCloseMenu}
          align="end"
          side={side}
          returnFocusTo={id}
          focusKey={`${id}-menu`}
        />
      ) : null}
    </div>
  );
}
